import { initReactI18next } from "react-i18next";
import i18n from "i18next";

import { IS_APP, IS_NODE_BROWSERLESS } from "@/__main__/constants.mjs";
import mainRefs from "@/__main__/refs.mjs";
import { JS_FILE_EXTENSION } from "@/app/constants.mjs";
import initOptions from "@/i18n/__init.mjs";
import { devDebug, devError } from "@/util/dev.mjs";
import dynamicImport from "@/util/dynamic-import.mjs";
import globals from "@/util/global-whitelist.mjs";
import { formatOrdinalNumber } from "@/util/i18n-helper.mjs";
import importBasePath from "@/util/import-base-path.mjs";
import { distance } from "@/util/string-compare.mjs";

const DEFAULT_LANGUAGE = "en";

async function initI18n() {
  await i18n
    .use(initReactI18next)
    .use({
      type: "languageDetector",
      async: false,
      detect: !IS_NODE_BROWSERLESS ? detectLanguage : () => DEFAULT_LANGUAGE,
    })
    .init(
      Object.assign(
        {
          fallbackLng: DEFAULT_LANGUAGE,
          resources: {},
          interpolation: {
            escapeValue: false,
            // TODO: i18n - this function is legacy in i18next>=21.3.0 and is blocking built-ins
            format: (value, format, lng, options) => {
              switch (format) {
                case "percent": {
                  return new Intl.NumberFormat(lng, {
                    style: "percent",
                    maximumFractionDigits: 1,
                    ...(options.formatParams ? options.formatParams : null),
                    ...(options.formatParams
                      ? options.formatParams[options.interpolationkey]
                      : null),
                  }).format(value);
                }
                case "timestamp": {
                  return new Date(
                    Number.parseInt(value, 10),
                  ).toLocaleDateString(lng, {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  });
                }
                case "number": {
                  return value || value === 0
                    ? new Intl.NumberFormat(lng, {
                        ...(options.formatParams ? options.formatParams : null),
                        ...(options.formatParams
                          ? options.formatParams[options.interpolationkey]
                          : null),
                      }).format(value)
                    : "-";
                }
                case "duration": {
                  return new Intl.NumberFormat(lng, {
                    style: "unit",
                    unit: options.unit,
                    unitDisplay: options.unitDisplay || "long",
                    ...(options.formatParams ? options.formatParams : null),
                    ...(options.formatParams
                      ? options.formatParams[options.interpolationkey]
                      : null),
                  }).format(value);
                }
                case "currency": {
                  const maximumFractionDigits = value % 1 === 0 ? 0 : 2;
                  return new Intl.NumberFormat(lng, {
                    style: "currency",
                    currency: "usd", // default value
                    maximumFractionDigits,
                    ...(options.formatParams ? options.formatParams : null),
                    ...(options.formatParams
                      ? options.formatParams[options.interpolationkey]
                      : null),
                  }).format(value);
                }
                case "datetime": {
                  return new Intl.DateTimeFormat(lng, {
                    ...(options.formatParams ? options.formatParams : null),
                    ...(options.formatParams
                      ? options.formatParams[options.interpolationkey]
                      : null),
                  }).format(value);
                }
                case "relativetime": {
                  return new Intl.RelativeTimeFormat(lng, {
                    ...(options.formatParams ? options.formatParams : null),
                    ...(options.formatParams
                      ? options.formatParams[options.interpolationkey]
                      : null),
                  }).format(value, options.unit);
                }
                case "ordinal": {
                  return formatOrdinalNumber(lng, value);
                }
                case "list": {
                  return new Intl.ListFormat(lng, {
                    ...(options.formatParams ? options.formatParams : null),
                    ...(options.formatParams
                      ? options.formatParams[options.interpolationkey]
                      : null),
                  }).format(value);
                }
                default:
                  break;
              }
              return value;
            },
          },
        },
        initOptions,
      ),
    );

  if (IS_NODE_BROWSERLESS) {
    await Promise.all(
      initOptions.languages.map((lng) => {
        return importLanguage(lng).then(({ default: resources }) => {
          addResources(resources);
        });
      }),
    );
  } else {
    const language = i18n.language;

    const { default: resources } = await importLanguage(language);

    addResources(resources);

    await i18n.changeLanguage(language);

    globals.document.documentElement.lang = language;

    devDebug(`language initialized as ${language}`, resources);
  }
}

function detectLanguage() {
  const preferredLanguage = globals?.localStorage?.getItem("PreferredLanguage");
  if (preferredLanguage) return preferredLanguage;

  if (!IS_APP) {
    const params = new URLSearchParams(globals.location.search);
    const lang = params.get("lang");
    return lang || globals.document.documentElement.lang;
  }

  const {
    navigator: { language = DEFAULT_LANGUAGE },
  } = globals;
  const { languages } = initOptions;

  const shortLang = language.substring(0, 2);

  const matches = languages
    .filter((l) => l.substring(0, 2) === shortLang)
    .map((l) => {
      return {
        language: l,
        distance: distance(language, l),
      };
    })
    .sort((a, b) => b.distance - a.distance);

  if (!matches.length)
    matches.push({
      language: DEFAULT_LANGUAGE,
    });

  const [{ language: matchedLanguage }] = matches;

  return matchedLanguage;
}
function importLanguage(language) {
  return dynamicImport(
    `${importBasePath(
      import.meta.url,
    )}i18n/__resources.${language}${JS_FILE_EXTENSION}`,
  );
}

function addResources(resources) {
  for (const language in resources) {
    const namespaces = resources[language];
    for (const namespace in namespaces) {
      const resource = namespaces[namespace];
      i18n.addResources(language, namespace, resource);
    }
  }
}

export async function changeLanguage(language) {
  if (i18n.language === language) return;

  // The language to switch to must have its module loaded, first.
  if (!i18n.getDataByLanguage(language)) {
    const { default: resources } = await importLanguage(language);

    addResources(resources);
  }

  await i18n.changeLanguage(language);

  // The main thing we are concerned about is re-fetching static data.
  await mainRefs.router.refetchData();

  globals.document.documentElement.lang = language;
  globals.localStorage.setItem("PreferredLanguage", language);
  devDebug(`language changed to ${language}`);
}

globals.__BLITZ_DEV__.i18n = i18n;

export { i18n };

export default (async () => {
  try {
    await initI18n();
  } catch (e) {
    devError("failed to init i18n", e);
  }
})();
